import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { authenticatedRoute } from "../_authenticated";
import { Card, CardContent, DataTable, Button } from "@/components/ui";
import { toErrorDisplayMessage, toDisplayString, toSafeReactNode } from "@/lib/utils";
import { useCaptures } from "@/hooks/useCaptures";
import type { CaptureItem } from "@/hooks/useCaptures";
import { useTeam } from "@/hooks/useTeam";

const PROVIDER_LABELS: Record<string, string> = {
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  salesforce: "Salesforce",
  weclapp: "Weclapp",
};

function LeadsPage() {
  const { t, i18n } = useTranslation("common");
  const [teamWide, setTeamWide] = useState(false);
  const [limit, setLimit] = useState(50);
  const { data: teamData } = useTeam();
  const hasTeam = teamData?.team != null;
  const { data, isLoading, error, isFetching } = useCaptures(limit, teamWide);
  const captures = data?.captures ?? [];
  const hasMore = !!data?.nextCursor;
  const locale = i18n.language || "en";

  const formatDateTime = (date: Date) =>
    new Date(date).toLocaleString(toDisplayString(locale), { dateStyle: "short", timeStyle: "short" });

  const columns = [
    {
      id: "capturedAt",
      header: toDisplayString(t("leads.date")),
      cell: (row: CaptureItem) => formatDateTime(new Date(row.capturedAt)),
    },
    {
      id: "sourceUrl",
      header: toDisplayString(t("leads.source")),
      cell: (row: CaptureItem) => {
        const url = toDisplayString(row.sourceUrl);
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline truncate max-w-[180px] block"
            title={toDisplayString(url)}
          >
            {url.replace(/^https?:\/\/(www\.)?/, "").slice(0, 40)}
            {url.length > 40 ? "…" : ""}
          </a>
        ) : (
          <span className="text-text-muted text-xs">{toSafeReactNode(t("leads.noSource"))}</span>
        );
      },
    },
    {
      id: "provider",
      header: toDisplayString(t("leads.provider")),
      cell: (row: CaptureItem) => (
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`h-2 w-2 rounded-full shrink-0 ${
              row.provider === "hubspot" ? "bg-amber-500" :
              row.provider === "pipedrive" ? "bg-green-500" :
              row.provider === "salesforce" ? "bg-blue-500" :
              "bg-teal-500"
            }`}
            aria-hidden
          />
          {toDisplayString(PROVIDER_LABELS[row.provider] ?? row.provider)}
        </span>
      ),
    },
    {
      id: "status",
      header: toDisplayString(t("leads.status")),
      cell: (row: CaptureItem) =>
        row.isDuplicate ? (
          <span className="inline-flex items-center gap-1 text-xs text-text-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-text-muted" aria-hidden />
            {toSafeReactNode(t("leads.duplicate"))}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-success">
            <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
            {toSafeReactNode(t("leads.new"))}
          </span>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("leads.title"))}</h1>
          <p className="text-sm text-text-muted">
            {teamWide ? toSafeReactNode(t("leads.subtitleTeam")) : toSafeReactNode(t("leads.subtitleMine"))}
            {captures.length > 0 && (
              <span className="ml-2 text-text-subtle">· {captures.length} {toSafeReactNode(t("leads.shown"))}</span>
            )}
          </p>
        </div>
        {hasTeam && (
          <Button
            variant={teamWide ? "primary" : "ghost"}
            size="sm"
            onClick={() => { setTeamWide((v) => !v); setLimit(50); }}
          >
            {teamWide ? toSafeReactNode(t("leads.myLeads")) : toSafeReactNode(t("leads.teamLeads"))}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="pt-4">
          {error && (
            <p className="text-sm text-danger mb-4">{toErrorDisplayMessage(error)}</p>
          )}
          <DataTable<CaptureItem>
            columns={columns}
            data={captures}
            keyFn={(row) => toDisplayString(row.id)}
            loading={isLoading}
            emptyMessage={toDisplayString(t("leads.emptyMessage"))}
          />

          {/* Load More */}
          {hasMore && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                disabled={isFetching}
                onClick={() => setLimit((l) => l + 50)}
              >
                {isFetching ? toSafeReactNode(t("leads.loading")) : toSafeReactNode(t("leads.loadMore"))}
              </Button>
            </div>
          )}

          {!hasMore && captures.length >= 50 && (
            <p className="mt-4 text-center text-xs text-text-subtle">
              {toSafeReactNode(t("leads.allLoaded", { count: captures.length }))}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const leadsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "leads",
  component: LeadsPage,
});
